import {
  PaymentsIntegrationView,
  QuickBookMappingView,
  QuickBooksMetaDataView,
  QuickbooksSettingsRequest,
  XeroMappingView,
  XeroMetaDataView,
  XeroSettingsRequest
} from "../../Contracts/Integration";
import { useRequestStore } from "../BackendStore";
import { create } from "zustand";
import { useOnboardingStore } from "../Onboarding";
import { useBannerStore } from "../Banner";
import { BannerType } from "../../Contracts/Banner";

const getConnectedIntegrationUrl = "/v1/payments/integrations/connected";
const deleteIntegrationUrl = "/v1/payments/integrations/{integrationId}";
const getQuickbooksMetaDataUrl = "/v1/payments/quickbooks/metadata";
const getXeroMetaDataUrl = "/v1/payments/xero/metadata";
const getSavedQuickbooksMetaDataUrl = "/v1/payments/quickbooks/mapping";
const getSavedXeroMetaDataUrl = "/v1/payments/xero/mapping";
const editOrCreateQuickbooksSettingsUrl = "/v1/payments/quickbooks/mapping/create-update";
const editOrCreateXeroSettingsUrl = "/v1/payments/xero/mapping/create-update";

interface IntegrationState {
  isLoading: boolean;
  integration?: PaymentsIntegrationView;
  getConnectedIntegration: (getAccessTokenSilently: any) => Promise<void>;
  deleteIntegration: (getAccessTokenSilently: any, integrationId: number) => Promise<void>;
  quickbooksMetaDataView?: QuickBooksMetaDataView;
  xeroMetaDataView?: XeroMetaDataView;
  savedQuickbooksMappingView?: QuickBookMappingView;
  savedXeroMappingView?: XeroMappingView;
  getQuickbooksMetaData: (getAccessTokenSilently: any) => Promise<void>;
  getXeroMetaData: (getAccessTokenSilently: any) => Promise<void>;
  getSavedQuickbooksMetaData: (getAccessTokenSilently: any) => Promise<void>;
  getSavedXeroMetaData: (getAccessTokenSilently: any) => Promise<void>;
  createOrUpdateQuickbooksSettings: (
    getAccessTokenSilently: any,
    quickbooksSettingsRequest: QuickbooksSettingsRequest
  ) => Promise<void>;
  createOrUpdateXeroSettings: (
    getAccessTokenSilently: any,
    xeroSettingsRequest: XeroSettingsRequest
  ) => Promise<void>;
}

export const useIntegrationStore = create<IntegrationState>((set, get) => {
  const { getRequest, postRequest, deleteRequest } = useRequestStore.getState();
  const { getAdyenKYCDetails } = useOnboardingStore.getState();
  const { addBanner } = useBannerStore.getState();

  const getConnectedIntegration = async (getAccessTokenSilently: any) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<PaymentsIntegrationView>(
        getAccessTokenSilently,
        getConnectedIntegrationUrl
      );
      await getAdyenKYCDetails(getAccessTokenSilently);
      set({ integration: response, isLoading: false });
    } catch (error) {
      console.error("Error fetching connected integration", error);
      set({ isLoading: false });
    }
  };

  const deleteIntegration = async (getAccessTokenSilently: any, integrationId: number) => {
    set({ isLoading: true });
    try {
      await deleteRequest(
        getAccessTokenSilently,
        deleteIntegrationUrl.replace("{integrationId}", String(integrationId))
      );
      await getAdyenKYCDetails(getAccessTokenSilently);
      addBanner({
        type: BannerType.Success,
        message: "Integration disconnected successfully"
      });
      set({ integration: undefined, isLoading: false });
    } catch (error) {
      console.error("Error deleting integration", error);
      set({ isLoading: false });
    }
  };

  const getQuickbooksMetaData = async (getAccessTokenSilently: any) => {
    try {
      set({ isLoading: true });
      const response = await getRequest<QuickBooksMetaDataView>(
        getAccessTokenSilently,
        getQuickbooksMetaDataUrl
      );
      set({ quickbooksMetaDataView: response, isLoading: false });
    } catch (error) {
      console.error("Error fetching Quickbooks metadata", error);
    }
  };

  const getXeroMetaData = async (getAccessTokenSilently: any) => {
    try {
      set({ isLoading: true });
      const response = await getRequest<XeroMetaDataView>(
        getAccessTokenSilently,
        getXeroMetaDataUrl
      );
      set({ xeroMetaDataView: response, isLoading: false });
    } catch (error) {
      console.error("Error fetching Xero metadata", error);
    }
  };

  const getSavedQuickbooksMetaData = async (getAccessTokenSilently: any) => {
    try {
      const response = await getRequest<QuickBookMappingView>(
        getAccessTokenSilently,
        getSavedQuickbooksMetaDataUrl
      );
      set({ savedQuickbooksMappingView: response });
    } catch (error) {
      console.error("Error fetching saved Quickbooks metadata", error);
    }
  };

  const getSavedXeroMetaData = async (getAccessTokenSilently: any) => {
    try {
      const response = await getRequest<XeroMappingView>(
        getAccessTokenSilently,
        getSavedXeroMetaDataUrl
      );
      set({ savedXeroMappingView: response });
    } catch (error) {
      console.error("Error fetching saved Xero metadata", error);
    }
  };

  const createOrUpdateQuickbooksSettings = async (
    getAccessTokenSilently: any,
    quickbooksSettingsRequest: QuickbooksSettingsRequest
  ) => {
    set({ isLoading: true });
    try {
      const response = await postRequest<QuickbooksSettingsRequest, QuickBookMappingView>(
        getAccessTokenSilently,
        editOrCreateQuickbooksSettingsUrl,
        quickbooksSettingsRequest
      );
      addBanner({
        type: BannerType.Success,
        message: "Quickbooks settings updated successfully"
      });
      set({ isLoading: false, savedQuickbooksMappingView: response });
    } catch (error) {
      console.error("Error creating or updating Quickbooks settings", error);
      set({ isLoading: false });
    }
  };

  const createOrUpdateXeroSettings = async (
    getAccessTokenSilently: any,
    xeroSettingsRequest: XeroSettingsRequest
  ) => {
    set({ isLoading: true });
    try {
      const response = await postRequest<XeroSettingsRequest, XeroMappingView>(
        getAccessTokenSilently,
        editOrCreateXeroSettingsUrl,
        xeroSettingsRequest
      );
      addBanner({
        type: BannerType.Success,
        message: "Xero settings updated successfully"
      });
      set({ isLoading: false, savedXeroMappingView: response });
    } catch (error) {
      console.error("Error creating or updating Xero settings", error);
      set({ isLoading: false });
    }
  };

  return {
    isLoading: false,
    getConnectedIntegration,
    deleteIntegration,
    getQuickbooksMetaData,
    getXeroMetaData,
    getSavedQuickbooksMetaData,
    getSavedXeroMetaData,
    createOrUpdateQuickbooksSettings,
    createOrUpdateXeroSettings
  };
});
